// src/pages/brand-collaborations-client-stories/components/FeaturedPartnerships.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FeaturedPartnerships = ({ activeFilter }) => {
  const [selectedPartnership, setSelectedPartnership] = useState(null);

  const partnerships = [
    {
      id: 1,
      category: 'fortune500',
      client: "Global Technology Corporation",
      industry: "Technology",
      project: "Digital Transformation Campaign",
      duration: "18 months",
      repeatProjects: 5,
      results: {
        engagement: "450% increase",
        brandLift: "300% improvement",
        roi: "800% return"
      },
      image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?q=80&w=2069&auto=format&fit=crop",
      logo: "https://images.pixabay.com/photo/2018/05/08/08/44/artificial-intelligence-3382507_960_720.jpg",
      testimonial: "The Mechanic Photography Studio transformed our brand narrative with exceptional creative vision and flawless execution.",
      executive: "Sarah Chen, CMO"
    },
    {
      id: 2,
      category: 'luxury-real-estate',
      client: "Platinum Properties International",
      industry: "Real Estate",
      project: "Luxury Development Showcase",
      duration: "24 months",
      repeatProjects: 8,
      results: {
        sales: "250% increase",
        viewings: "500% more virtual tours",
        premiumPricing: "30% price elevation"
      },
      image: "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=1600",
      logo: "https://images.pixabay.com/photo/2016/11/29/09/37/architecture-1868667_960_720.jpg",
      testimonial: "Their ability to capture the essence of luxury while maintaining architectural authenticity is unmatched in the industry.",
      executive: "Michael Rodriguez, Development Director"
    },
    {
      id: 3,
      category: 'high-profile-events',
      client: "Elite Corporate Events",
      industry: "Events & Hospitality",
      project: "Global Summit Documentation",
      duration: "36 months",
      repeatProjects: 12,
      results: {
        coverage: "360° comprehensive documentation",
        satisfaction: "99.8% client approval",
        efficiency: "50% faster delivery"
      },
      image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2070&auto=format&fit=crop",
      logo: "https://images.pexels.com/photos/1709003/pexels-photo-1709003.jpeg?auto=compress&cs=tinysrgb&w=800",
      testimonial: "Professional excellence, creative innovation, and seamless integration with our event teams. Truly exceptional partnership.",
      executive: "James Thompson, Event Director"
    },
    {
      id: 4,
      category: 'fortune500',
      client: "International Manufacturing Group",
      industry: "Manufacturing",
      project: "Innovation Showcase Series",
      duration: "30 months",
      repeatProjects: 7,
      results: {
        reach: "2M+ global impressions",
        engagement: "600% social media growth",
        leads: "400% qualified inquiries"
      },
      image: "https://images.pixabay.com/photo/2017/08/10/02/05/people-2619817_960_720.jpg",
      logo: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop",
      testimonial: "Strategic creative partnership that consistently delivers beyond expectations with measurable business impact.",
      executive: "David Kim, VP Marketing"
    },
    {
      id: 5,
      category: 'luxury-real-estate',
      client: "Prestige Residential Developments",
      industry: "Luxury Real Estate",
      project: "Exclusive Property Portfolio",
      duration: "42 months",
      repeatProjects: 15,
      results: {
        sales: "350% faster closings",
        premiumPositioning: "40% price elevation",
        marketShare: "25% market leadership"
      },
      image: "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=1600",
      logo: "https://images.pixabay.com/photo/2016/11/18/17/46/house-1836070_960_720.jpg",
      testimonial: "Exceptional visual storytelling that elevates our properties and connects with discerning clientele authentically.",
      executive: "Lisa Anderson, Sales Director"
    },
    {
      id: 6,
      category: 'high-profile-events',
      client: "Global Business Forum",
      industry: "Professional Services",
      project: "Annual Conference Series",
      duration: "48 months",
      repeatProjects: 20,
      results: {
        attendance: "300% growth",
        engagement: "500% social reach",
        sponsorship: "250% revenue increase"
      },
      image: "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?q=80&w=2070&auto=format&fit=crop",
      logo: "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&w=800",
      testimonial: "Trusted creative partner delivering consistent excellence across multiple high-stakes corporate events annually.",
      executive: "Robert Williams, Conference Director"
    }
  ];

  const filteredPartnerships = activeFilter === 'all' 
    ? partnerships 
    : partnerships.filter(p => p.category === activeFilter || activeFilter === 'repeat-clients');

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
            Featured <span className="text-gradient-gold">Partnerships</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Strategic collaborations with industry leaders, showcasing long-term relationships 
            built on trust, quality, and measurable business impact
          </p>
        </div>

        {/* Partnerships Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPartnerships.map((partnership) => (
            <div
              key={partnership.id}
              className="bg-white rounded-lg overflow-hidden shadow-subtle hover:shadow-elevation transition-all duration-300 cursor-pointer hover-lift"
              onClick={() => setSelectedPartnership(partnership)}
            >
              {/* Partnership Image */}
              <div className="relative h-48">
                <Image
                  src={partnership.image}
                  alt={partnership.project}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute top-4 right-4">
                  <span className="bg-accent text-white px-3 py-1 rounded-full text-sm font-medium">
                    {partnership.repeatProjects} Projects
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-white font-bold text-lg mb-1">{partnership.client}</h3>
                  <p className="text-white/80 text-sm">{partnership.industry}</p>
                </div>
              </div>

              {/* Partnership Info */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-accent font-semibold text-sm">{partnership.project}</span>
                  <span className="text-text-secondary text-xs">{partnership.duration}</span>
                </div>

                {/* Key Results */}
                <div className="space-y-2 mb-4">
                  {Object.entries(partnership.results).slice(0, 2).map(([key, value], index) => (
                    <div key={index} className="flex items-center text-sm">
                      <Icon name="TrendingUp" size={14} className="text-accent mr-2" strokeWidth={2} />
                      <span className="text-text-secondary">{value}</span>
                    </div>
                  ))}
                </div>

                {/* Action Button */}
                <button className="w-full bg-surface text-text-primary py-2 rounded-lg font-medium text-sm hover:bg-accent hover:text-white transition-all duration-300">
                  View Full Story
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Partnership Modal */}
        {selectedPartnership && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="relative h-64">
                <Image
                  src={selectedPartnership.image}
                  alt={selectedPartnership.project}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <button
                  onClick={() => setSelectedPartnership(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <Icon name="X" size={20} strokeWidth={2} />
                </button>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-white font-bold text-2xl mb-2">{selectedPartnership.client}</h3>
                  <p className="text-white/80">{selectedPartnership.project}</p>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-8">
                {/* Partnership Details */}
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h4 className="font-bold text-lg mb-4">Partnership Overview</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Industry:</span>
                        <span className="font-medium">{selectedPartnership.industry}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Duration:</span>
                        <span className="font-medium">{selectedPartnership.duration}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-secondary">Repeat Projects:</span>
                        <span className="font-medium">{selectedPartnership.repeatProjects}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-4">Measurable Results</h4>
                    <div className="space-y-3">
                      {Object.entries(selectedPartnership.results).map(([key, value], index) => (
                        <div key={index} className="flex items-center">
                          <Icon name="CheckCircle" size={16} className="text-accent mr-3" strokeWidth={2} />
                          <span className="text-text-secondary">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Testimonial */}
                <div className="bg-surface rounded-lg p-6">
                  <div className="flex items-start space-x-4">
                    <Icon name="Quote" size={24} className="text-accent flex-shrink-0 mt-1" strokeWidth={2} />
                    <div>
                      <p className="text-text-primary text-lg italic mb-3">
                        "{selectedPartnership.testimonial}"
                      </p>
                      <p className="text-text-secondary font-medium">
                        — {selectedPartnership.executive}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedPartnerships;